import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { ArrowLeft, Award, CalendarDays, Clock, Layers, Lock, Sparkles, Target, Trophy } from "lucide-react";
import { api, fmtDate, ghs } from "../../lib/api";
import { EmptyState, StatusBadge } from "../../components/site/Bits";
import { FullLoader } from "../../components/site/Guards";

const ICONS = { football: Trophy, correct_score: Target, package: Layers, daily: CalendarDays, premium: Award, other: Sparkles };

const Header = () => (
  <div className="relative overflow-hidden rounded-3xl border border-amber-400/15 bg-[#14110a] p-8 sm:p-10">
    <div className="absolute -right-24 -top-24 h-72 w-72 rounded-full bg-amber-500/15 blur-[90px]" />
    <p className="relative text-xs font-semibold uppercase tracking-[0.2em] text-amber-400">Separate service</p>
    <h1 className="relative mt-3 text-3xl font-extrabold text-white sm:text-5xl" data-testid="predictions-title">GET PREDICTIONS NOW</h1>
    <p className="relative mt-3 max-w-xl text-slate-300">Access football predictions and correct-score packages. This area is independent of the Raphanex digital-products store.</p>
  </div>
);

export function PredictionsHub() {
  const [d, setD] = useState(null);
  useEffect(() => { api.get("/predictions/overview").then((r) => setD(r.data)).catch(() => setD({ categories: [], my_access: [] })); }, []);
  if (!d) return <FullLoader />;
  return (
    <div className="space-y-10">
      <Header />
      {d.enabled === false ? <EmptyState icon={Clock} testId="predictions-disabled" title="Coming soon" text={d.message} /> : (
        <>
          <div className="grid gap-5 sm:grid-cols-2 lg:grid-cols-3" data-testid="prediction-categories">
            {d.categories.map((c) => {
              const I = ICONS[c.key];
              return (
                <Link key={c.key} to={`/predictions/c/${c.key}`} className="card-rx card-hover p-7" data-testid={`prediction-category-${c.key}`}>
                  <div className="flex items-start justify-between">
                    <span className="grid h-11 w-11 place-items-center rounded-xl border border-amber-400/20 bg-amber-400/10 text-amber-300"><I size={20} /></span>
                    {c.package_count > 0 ? <StatusBadge status="active" label={`${c.package_count} available`} /> : <StatusBadge status="soon" label="Coming soon" />}
                  </div>
                  <h3 className="mt-6 text-lg font-semibold text-white">{c.title}</h3>
                  <p className="mt-1.5 text-sm text-slate-400">{c.description}</p>
                </Link>
              );
            })}
          </div>
          <div>
            <h2 className="text-xl font-semibold text-white">My prediction access</h2>
            <div className="mt-5">
              {d.my_access.length === 0 ? <EmptyState icon={Lock} testId="prediction-access-empty" title="No active packages" text="Packages you purchase will be listed here with their access period." /> : (
                <div className="grid gap-4 md:grid-cols-2">
                  {d.my_access.map((a) => (
                    <Link key={a.transaction_ref} to={`/predictions/p/${a.package_id}`} className="card-rx card-hover p-5">
                      <p className="font-semibold text-white">{a.package_name}</p>
                      <p className="mt-1 text-xs text-slate-500">Active until {fmtDate(a.expires_at, true)}</p>
                    </Link>
                  ))}
                </div>
              )}
            </div>
          </div>
        </>
      )}
    </div>
  );
}

export function PredictionCategory() {
  const { cat } = useParams();
  const [items, setItems] = useState(null);
  const [meta, setMeta] = useState(null);
  useEffect(() => {
    api.get("/predictions/packages", { params: { category: cat } }).then((r) => setItems(r.data)).catch(() => setItems([]));
    api.get("/predictions/overview").then((r) => setMeta(r.data.categories.find((c) => c.key === cat))).catch(() => {});
  }, [cat]);
  return (
    <div className="space-y-8">
      <Link to="/predictions" className="inline-flex items-center gap-2 text-sm text-slate-400 hover:text-white" data-testid="prediction-back-link"><ArrowLeft size={16} /> GET PREDICTIONS NOW</Link>
      <div><p className="text-xs font-semibold uppercase tracking-[0.2em] text-amber-400">Predictions</p><h1 className="mt-2 text-3xl font-bold text-white sm:text-4xl">{meta?.title || "Packages"}</h1><p className="mt-2 text-slate-400">{meta?.description}</p></div>
      {items === null ? <FullLoader /> : items.length === 0 ? (
        <EmptyState icon={Clock} testId="prediction-category-empty" title="Coming soon" text="Packages in this category are being prepared and will be available here soon." />
      ) : (
        <div className="grid gap-5 sm:grid-cols-2 lg:grid-cols-3">
          {items.map((p) => (
            <Link key={p.id} to={`/predictions/p/${p.id}`} className="card-rx card-hover flex flex-col p-6" data-testid={`prediction-package-${p.id}`}>
              <h3 className="text-lg font-semibold text-white">{p.name}</h3>
              <p className="mt-1.5 flex-1 text-sm text-slate-400">{p.short_description}</p>
              <div className="mt-5 flex items-center justify-between"><span className="font-mono-rx font-semibold text-amber-400">{ghs(p.price_minor)}</span><span className="text-xs text-slate-500">{p.duration_days} day(s)</span></div>
            </Link>
          ))}
        </div>
      )}
    </div>
  );
}

export function PredictionPackage() {
  const { id } = useParams();
  const [d, setD] = useState(null);
  const [missing, setMissing] = useState(false);
  useEffect(() => { api.get(`/predictions/packages/${id}`).then((r) => setD(r.data)).catch(() => setMissing(true)); }, [id]);
  if (missing) return <EmptyState title="Package not found" action={<Link to="/predictions" className="btn-ghost">Back</Link>} />;
  if (!d) return <FullLoader />;
  const p = d.package;
  return (
    <div className="mx-auto max-w-3xl space-y-8">
      <Link to={`/predictions/c/${p.category}`} className="inline-flex items-center gap-2 text-sm text-slate-400 hover:text-white"><ArrowLeft size={16} /> Back</Link>
      <div className="card-rx p-8">
        <h1 className="text-3xl font-bold text-white" data-testid="prediction-package-name">{p.name}</h1>
        <p className="mt-2 text-slate-400">{p.short_description}</p>
        <div className="mt-6 flex flex-wrap items-center gap-6"><span className="font-mono-rx text-2xl font-semibold text-amber-400">{ghs(p.price_minor)}</span><span className="text-sm text-slate-400">{p.duration_days} day(s) access</span></div>
        {p.description && <p className="mt-6 whitespace-pre-line text-sm leading-relaxed text-slate-300">{p.description}</p>}
        <div className="mt-8">
          {d.has_access ? (
            <EmptyState icon={Clock} testId="prediction-content-pending" title="You have access" text="Prediction content for this package will appear here as soon as it is published." />
          ) : (
            <Link to={`/checkout/prediction_package/${p.id}`} className="btn-gold" data-testid="prediction-buy-btn"><Lock size={16} /> Buy package</Link>
          )}
        </div>
      </div>
    </div>
  );
}
